import { useUserConfigurations } from "@via/api/configuration";
import { PageHeading } from "@via/components/page-heading.tsx";
import { useWorkspaces } from "@via/pages/agent-workspaces/workspaces.api.tsx";
import { PAGE_NAMES, ROUTES } from "@via/utils/constants.tsx";
import { type JSX } from "react";
import { Link } from "react-router-dom";

const BYTES_PER_MB = 1048576;

/** Disk use in megabytes (1 MB is 1048576 bytes, as the quota counts it), one decimal place. */
export const diskUsedText = (bytes: number | null): string =>
  bytes === null
    ? "not measured yet"
    : `${(bytes / BYTES_PER_MB).toLocaleString("en-US", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })} MB`;

const STATE_TEXT = {
  provisioning: "Being created",
  ready: "Ready",
  archived: "Archived",
  destroyed: "Removed",
} as const;

/**
 * The signed in person's workspaces, newest first (agent-7-4, agent-d73). A workspace is created
 * by an AI client through Foundry Connect's tools; this page is where a person sees them, edits
 * their mounts, follows their jobs and approves the ones that wait for approval.
 */
export default function AgentWorkspaces(): JSX.Element | null {
  const { data: configurations } = useUserConfigurations();
  const enabled = Boolean(configurations?.AGENT_WORKSPACE_JOBS_ENABLED);
  const workspaces = useWorkspaces(enabled);

  if (!enabled) return null;

  const rows = workspaces.data ?? [];
  return (
    <main className="space-y-4 p-4">
      <PageHeading title={PAGE_NAMES.WORKSPACES} />
      <p className="text-sm text-gray-600">
        Workspaces are folders on your clusters where an AI client runs jobs in
        containers. Open one to choose the folders its jobs see and to follow
        its jobs.
      </p>
      {workspaces.isLoading ? (
        <p className="text-sm text-gray-600">Loading workspaces</p>
      ) : workspaces.isError ? (
        <p className="text-sm text-gray-600">
          Your workspaces could not be loaded.
        </p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-600">You have no workspaces yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead>
              <tr className="text-left text-gray-900">
                <th className="px-3 py-2 font-semibold">Name</th>
                <th className="px-3 py-2 font-semibold">State</th>
                <th className="px-3 py-2 font-semibold">Image</th>
                <th className="px-3 py-2 font-semibold">Disk used</th>
                <th className="px-3 py-2 font-semibold">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-gray-700">
              {rows.map(workspace => (
                <tr key={workspace.workspaceId}>
                  <td className="px-3 py-2">
                    <Link
                      className="font-semibold text-indigo-600 hover:text-indigo-500"
                      to={`${ROUTES.WORKSPACES}/${workspace.workspaceId}`}>
                      {workspace.name}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{STATE_TEXT[workspace.state]}</td>
                  <td className="break-all px-3 py-2 font-mono">
                    {workspace.image}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    {diskUsedText(workspace.storageBytes)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    {new Date(workspace.createdAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
